import { useForm, Controller, FormProvider } from "react-hook-form";
import { zodV3Resolver as zodResolver } from "@/lib/zodV3Resolver";
import { useMutation, useQuery } from "@apollo/client";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import EmailPreviewDialog, { EmailPreviewModalActions } from "@/components/EmailPreviewDialog";
import TextField from "@/components/TextField";
import FileUpload from "@/components/FileUpload/FileUpload";
import Autocomplete from "@/components/Autocomplete";
import AvatarDetails from "@/components/AvatarDetails";
import Avatar from "@/components/Avatar";
import IconButton from "@/components/IconButton";
import { useEffect, useRef, useState } from "react";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import classNames from "classnames";
import { createJobApplicationSchema, CreateJobApplicationFormData } from "./schema";
import DynamicQuestionRenderer from "./DynamicQuestionRenderer";
import { JobPost, isExecuSearchServiceLevel } from "../../types";
import { CREATE_JOB_APPLICATION, JOB_POST_WITH_APPLICATIONS_QUERY } from "../../queries";
import Select from "@/components/Select";
import MultiSelect from "@/components/MultiSelect/MultiSelect";
import { ALL_US_STATES_ABBRV } from "@/lib/usStateHelpers";
import { PERSON_CONTACT_AUTOFILL_QUERY } from "@/pages/people/queries";
import { useSomePeopleQuery } from "@/pages/people/_hooks/useSomePeopleQuery";
import {
  AlgoliaQuickSearch,
  type PersonHit,
  createPersonOnlySearchUiState,
  getPersonDisplayName
} from "@/components/AlgoliaSearch";
import { useFlags } from "@/hooks/useFeatureFlags";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { useJobApplicationConfirmationEmailPreview } from "../../_hooks/useJobApplicationConfirmationEmailPreview";

interface CreateJobApplicationDialogProps {
  open: boolean;
  onClose: () => void;
  jobPost: JobPost;
  onSuccess?: () => void;
}

function renderPersonOnlyHitWithEmail(hit: PersonHit, select: (hit: PersonHit) => void) {
  const textParts = [hit.email, hit.facility_name].filter(Boolean);
  const text = textParts.length > 0 ? textParts.join(" • ") : "";
  return (
    <div key={hit.objectID} className="mb-2 cursor-pointer" onClick={() => select(hit)}>
      <AvatarDetails
        title={hit.name}
        text={text}
        picture={hit.profile_photo}
        secondaryText={hit.pga_id ? `PGA ID: ${hit.pga_id}` : hit.section_name || ""}
      />
    </div>
  );
}

export default function CreateJobApplicationDialog({
  open,
  onClose,
  jobPost,
  onSuccess
}: CreateJobApplicationDialogProps) {
  const isExecuSearch = isExecuSearchServiceLevel(jobPost.serviceLevel);

  const [resumeError, setResumeError] = useState<string | undefined>(undefined);
  const [coverLetterError, setCoverLetterError] = useState<string | undefined>(undefined);
  const [personError, setPersonError] = useState<string | undefined>(undefined);

  // Tracks successful submission to prevent the "unsaved changes" confirm dialog
  // from firing due to stale isDirty closure when onClosed fires after reset()
  const submittedSuccessfully = useRef(false);

  const flags = useFlags();
  const useAlgoliaPeople = (flags as Record<string, boolean>)["backoffice-algolia-person-index"];
  const canViewPersonContact = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonContact
  });

  const [peopleVariables, setPeopleVariables] = useState<{ search?: string }>({});
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [personSearchQuery, setPersonSearchQuery] = useState<string>("");
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = useJobApplicationConfirmationEmailPreview();

  const { data: personData, loading: peopleLoading } = useSomePeopleQuery({
    variables: { input: peopleVariables },
    skip: useAlgoliaPeople || !peopleVariables.search || peopleVariables.search.trim() === ""
  });

  const selectedPgaId = selectedPerson?.pgaId;
  const { data: contactAutofillData } = useQuery(PERSON_CONTACT_AUTOFILL_QUERY, {
    variables: { pgaId: selectedPgaId ?? "" },
    skip: !open || !selectedPgaId || !canViewPersonContact,
    fetchPolicy: "network-only"
  });

  const methods = useForm<CreateJobApplicationFormData>({
    resolver: zodResolver(createJobApplicationSchema(isExecuSearch)),
    defaultValues: {
      pgaId: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      website: "",
      city: "",
      state: "",
      currentOrMostRecentPosition: "",
      currentOrMostRecentEmployer: "",
      referralSource: [],
      resume: undefined,
      coverLetter: undefined,
      customAnswers: []
    }
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset,
    setValue,
    getValues,
    watch,
    resetField
  } = methods;

  const emailValue = watch("email");
  const canPreviewEmail = Boolean(
    selectedPerson && (emailValue?.trim() || selectedPerson.pgaId || selectedPerson.email)
  );

  const referralSourceOptions =
    jobPost.applicationForm?.referralSourceOptions?.map((source: string) => ({
      value: source,
      label: source
    })) ?? [];

  const [createJobApplication, { loading }] = useMutation(CREATE_JOB_APPLICATION, {
    refetchQueries: [
      {
        query: JOB_POST_WITH_APPLICATIONS_QUERY,
        variables: { jobPostId: jobPost.id }
      }
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      if (data?.createJobApplication?.success) {
        showSnackbar("Job application created", { close: true });
        submittedSuccessfully.current = true;
        reset();
        onSuccess?.();
        onClose();
      } else {
        const message = data?.createJobApplication?.message;
        if (message) {
          showErrorSnackbar("Application not created", { subtitle: message });
        } else {
          showErrorSnackbar("Application not created", { subtitle: "Please try again later." });
        }
      }
    },
    onError: () => {
      showErrorSnackbar("Application not created", { subtitle: "Please try again later." });
    }
  });

  useEffect(() => {
    if (useAlgoliaPeople) return;

    const handler = setTimeout(() => {
      setPeopleVariables({ search: debouncedSearchTerm });
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [debouncedSearchTerm, useAlgoliaPeople]);

  useEffect(() => {
    if (open) {
      reset();
      setResumeError(undefined);
      setCoverLetterError(undefined);
      setPersonError(undefined);
      setPersonSearchQuery("");
      setDebouncedSearchTerm("");
      setPeopleVariables({});
      setSelectedPerson(null);
    }
  }, [open, reset]);

  const populateFromPerson = (person: any) => {
    setSelectedPerson(person);
    setPersonError(undefined);

    if (person.email != null && person.email !== "") {
      setValue("email", person.email);
    }
    const primaryOrFirstPhone =
      person.phoneNumbers?.find((p: any) => p.phoneType === "Primary")?.phoneNumber ||
      person.phoneNumbers?.[0]?.phoneNumber;
    if (primaryOrFirstPhone) {
      setValue("phone", primaryOrFirstPhone);
    }

    if (isExecuSearch) {
      const primaryAddress = person.addresses?.[0];
      if (primaryAddress?.city) {
        setValue("city", primaryAddress.city);
      }
      if (primaryAddress?.state) {
        setValue("state", primaryAddress.state);
      }

      const primaryEmployment = person.employments?.find((e: any) => e.isPrimary) || person.employments?.[0];
      if (primaryEmployment?.facility?.name) {
        setValue("currentOrMostRecentEmployer", primaryEmployment.facility.name);
      }
    }
  };

  useEffect(() => {
    const contact = contactAutofillData?.person;
    if (!contact) return;

    setSelectedPerson((prev: any) =>
      prev
        ? {
            ...prev,
            email: contact.email ?? prev.email,
            phoneNumbers: contact.phoneNumbers?.length ? contact.phoneNumbers : prev.phoneNumbers,
            addresses: contact.addresses?.length ? contact.addresses : prev.addresses
          }
        : prev
    );

    if (!getValues("email") && contact.email) {
      setValue("email", contact.email);
    }
    const primaryOrFirstPhone =
      contact.phoneNumbers?.find((p) => p.phoneType === "Primary")?.phoneNumber ||
      contact.phoneNumbers?.[0]?.phoneNumber;
    if (!getValues("phone") && primaryOrFirstPhone) {
      setValue("phone", primaryOrFirstPhone);
    }
    if (isExecuSearch) {
      const primaryAddress = contact.addresses?.[0];
      if (!getValues("city") && primaryAddress?.city) {
        setValue("city", primaryAddress.city);
      }
      if (!getValues("state") && primaryAddress?.state) {
        setValue("state", primaryAddress.state);
      }
    }
  }, [contactAutofillData, getValues, isExecuSearch, setValue]);

  const onSubmit = async (formData: CreateJobApplicationFormData) => {
    if (!selectedPerson) {
      setPersonError("Please select a person from the search");
      return;
    }

    setPersonError(undefined);

    const attachments = [];
    if (formData.resume) {
      attachments.push({ type: "resume" as const, file: formData.resume });
    }
    if (formData.coverLetter) {
      attachments.push({ type: "cover_letter" as const, file: formData.coverLetter });
    }

    const answers = formData.customAnswers
      ?.map((answer) => ({
        questionId: answer.questionId,
        optionIds: answer.selectedOptionId
          ? [answer.selectedOptionId]
          : answer.selectedOptionIds?.length
            ? answer.selectedOptionIds
            : undefined,
        customAnswer: answer.textAnswer || undefined
      }))
      .filter((a) => a.optionIds || a.customAnswer);

    const emailFromForm = formData.email?.trim() || undefined;
    const phoneFromForm = formData.phone?.trim() || undefined;
    const emailAddress = emailFromForm || selectedPerson.email || undefined;
    const phoneNumber =
      phoneFromForm ||
      selectedPerson.phoneNumbers?.find((p: any) => p.phoneType === "Primary")?.phoneNumber ||
      selectedPerson.phoneNumbers?.[0]?.phoneNumber ||
      undefined;

    await createJobApplication({
      variables: {
        input: {
          jobPostId: jobPost.id,
          emailAddress,
          phoneNumber,
          website: formData.website || undefined,
          pgaId: selectedPerson.pgaId || undefined,
          attachments: attachments.length > 0 ? attachments : undefined,
          answers: answers && answers.length > 0 ? answers : undefined,
          ...(isExecuSearch && {
            city: formData.city,
            state: formData.state,
            currentOrMostRecentPosition: formData.currentOrMostRecentPosition,
            currentOrMostRecentEmployer: formData.currentOrMostRecentEmployer,
            referralSource: formData.referralSource
          })
        }
      }
    });
  };

  useEffect(() => {
    if (!open) {
      setPreviewOpen(false);
      resetPreview();
    }
  }, [open, resetPreview]);

  const handlePreviewEmail = () => {
    if (!selectedPerson) {
      setPersonError("Please select a person from the search");
      return;
    }

    const emailAddress = getValues("email")?.trim() || selectedPerson.email || undefined;
    if (!emailAddress && !selectedPerson.pgaId) {
      setPersonError("Applicant email or PGA ID is required to preview the confirmation email");
      return;
    }

    setPreviewOpen(true);
    void loadPreview({
      jobPostId: jobPost.id,
      pgaId: selectedPerson.pgaId || undefined,
      emailAddress
    });
  };

  const handleClose = () => {
    if (
      !submittedSuccessfully.current &&
      isDirty &&
      !window.confirm("You have unsaved changes. Are you sure you want to close?")
    ) {
      return;
    }
    submittedSuccessfully.current = false;
    reset();
    setResumeError(undefined);
    setCoverLetterError(undefined);
    setPersonError(undefined);
    setSelectedPerson(null);
    setPersonSearchQuery("");
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClosed={handleClose}
        headline="Add Job Application"
        className="w-[800px] max-w-[90vw]"
        hasFormElements
        actions={
          <EmailPreviewModalActions
            onPreview={handlePreviewEmail}
            previewDisabled={isSubmitting || loading || !canPreviewEmail}
          >
            <Button variant="text" onClick={handleClose} disabled={isSubmitting || loading}>
              Cancel
            </Button>
            <Button
              variant="filled"
              iconClassName={classNames((isSubmitting || loading) && "animate-spin w-6 h-6 !block")}
              icon={isSubmitting || loading ? "progress_activity" : undefined}
              onClick={() => {
                if (!selectedPerson) {
                  setPersonError("Please select a person from the search");
                }
                handleSubmit(onSubmit)();
              }}
              disabled={isSubmitting || loading}
            >
              Create Application
            </Button>
          </EmailPreviewModalActions>
        }
      >
        <div className="px-6">
          <FormProvider {...methods}>
            <form className="flex flex-col gap-6 max-h-[calc(80vh-200px)] overflow-y-auto pr-2 -mr-2">
              <div>
                <div className="text-title-medium mb-2">Person Information</div>
                <div className="space-y-2">
                  {!selectedPerson && (
                    <>
                      {useAlgoliaPeople ? (
                        <AlgoliaQuickSearch<PersonHit>
                          indexName={import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME}
                          label="Search for a Person"
                          placeholder="Search by name, PGA ID, or email"
                          defaultValue={personSearchQuery}
                          initialUiState={createPersonOnlySearchUiState(
                            import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME
                          )}
                          renderHit={renderPersonOnlyHitWithEmail}
                          getDisplayValue={getPersonDisplayName}
                          onSelect={(hit) => {
                            const fullName = getPersonDisplayName(hit);
                            setPersonSearchQuery(fullName);

                            const nameParts = (hit.preferred_name || hit.name || "").split(" ");
                            const firstName = nameParts[0] || "";
                            const lastName = nameParts.slice(1).join(" ") || "";

                            const personData = {
                              pgaId: hit.pga_id,
                              firstName,
                              lastName,
                              email: hit.email,
                              phoneNumbers: hit.phone ? [{ phoneNumber: hit.phone, phoneType: "Primary" }] : [],
                              profilePhoto: hit.profile_photo,
                              addresses:
                                hit.city && hit.state
                                  ? [
                                      {
                                        city: hit.city,
                                        state: hit.state,
                                        addressType: "Primary"
                                      }
                                    ]
                                  : [],
                              employments: hit.facility_name
                                ? [
                                    {
                                      title: "",
                                      facility: { name: hit.facility_name },
                                      isPrimary: true
                                    }
                                  ]
                                : []
                            };

                            populateFromPerson(personData);
                          }}
                          onClear={() => {
                            setPersonSearchQuery("");
                            setSelectedPerson(null);
                          }}
                          error={!!personError}
                          errorText={personError}
                        />
                      ) : (
                        <Autocomplete
                          label="Search for a Person"
                          query={personSearchQuery}
                          loadingResults={peopleLoading}
                          required
                          error={!!personError}
                          errorText={personError}
                          data={
                            (personData?.people?.nodes || []) as Array<{
                              id: string;
                              firstName: string;
                              lastName: string;
                              pgaId: string;
                              primarySectionAffiliation: { section: { name: string } };
                              primaryFacility: { name: string };
                              profilePhoto: string;
                            }>
                          }
                          onChangeText={(searchText) => {
                            setPersonSearchQuery(searchText);
                            setDebouncedSearchTerm(searchText);
                            if (searchText === "") {
                              setSelectedPerson(null);
                            }
                          }}
                          reset={() => {
                            setPersonSearchQuery("");
                            setPeopleVariables({ search: "" });
                            setSelectedPerson(null);
                          }}
                          onSelect={(selected) => {
                            setPersonSearchQuery(`${selected.firstName} ${selected.lastName}`);
                            populateFromPerson(selected);
                          }}
                          renderItem={(data: {
                            id: string;
                            firstName: string;
                            lastName: string;
                            pgaId: string;
                            primarySectionAffiliation: { section: { name: string } };
                            primaryFacility: { name: string };
                            profilePhoto: string;
                          }) => (
                            <AvatarDetails
                              title={`${data.firstName} ${data.lastName}`}
                              text={data.primaryFacility?.name || ""}
                              picture={data.profilePhoto}
                              secondaryText={data.primarySectionAffiliation?.section?.name || ""}
                            />
                          )}
                        />
                      )}
                    </>
                  )}
                  {selectedPerson && (
                    <div className="bg-sys-brand-surface-container-low px-4 py-2 rounded flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center">
                          <Avatar
                            picture={selectedPerson.profilePhoto || undefined}
                            iconName="person"
                            className="min-w-[3rem]"
                          />
                          <div className="ml-2">
                            {selectedPerson.primarySectionAffiliation?.section?.name && (
                              <div className="text-body-small text-sys-brand-on-surface-variant">
                                {selectedPerson.primarySectionAffiliation.section.name}
                              </div>
                            )}
                            <div className="text-body-medium">
                              {selectedPerson.firstName} {selectedPerson.lastName}
                            </div>
                            {selectedPerson.pgaId && (
                              <div className="text-body-small text-sys-brand-on-surface-variant">
                                PGA ID: {selectedPerson.pgaId} {selectedPerson.email && `• ${selectedPerson.email}`}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <IconButton
                        name="close"
                        title="Clear selected person"
                        skipMargin
                        onClick={(e: Event) => {
                          e.preventDefault();
                          setSelectedPerson(null);
                          setPersonSearchQuery("");
                          setDebouncedSearchTerm("");
                          setPeopleVariables({});
                          setPersonError(undefined);
                          setValue("email", "");
                          setValue("phone", "");
                          if (isExecuSearch) {
                            setValue("city", "");
                            setValue("state", "");
                            setValue("currentOrMostRecentEmployer", "");
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Email"
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors.email}
                      errorText={errors.email?.message}
                      className="min-w-0"
                    />
                  )}
                />
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Phone"
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors.phone}
                      errorText={errors.phone?.message}
                      className="min-w-0"
                    />
                  )}
                />
              </div>

              {isExecuSearch && (
                <div>
                  <div className="text-title-medium mb-4">Application Details</div>
                  <div className="flex flex-col gap-6">
                    <div className="grid grid-cols-[2fr_1fr] gap-2">
                      <Controller
                        name="city"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            label="City"
                            value={field.value}
                            onChange={field.onChange}
                            required
                            error={!!errors.city}
                            errorText={errors.city?.message}
                            className="min-w-0"
                          />
                        )}
                      />
                      <Controller
                        name="state"
                        control={control}
                        render={({ field }) => (
                          <Select
                            label="State"
                            value={field.value}
                            onSelect={field.onChange}
                            options={ALL_US_STATES_ABBRV.map((s) => ({ value: s, label: s }))}
                            required
                            error={!!errors.state}
                            errorText={errors.state?.message}
                            className="min-w-0"
                          />
                        )}
                      />
                    </div>
                    <Controller
                      name="currentOrMostRecentPosition"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          className="flex-1"
                          label="Current or Most Recent Position"
                          value={field.value}
                          onChange={field.onChange}
                          required
                          error={!!errors.currentOrMostRecentPosition}
                          errorText={errors.currentOrMostRecentPosition?.message}
                        />
                      )}
                    />
                    <Controller
                      name="currentOrMostRecentEmployer"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          className="flex-1"
                          label="Current or Most Recent Employer"
                          value={field.value}
                          onChange={field.onChange}
                          required
                          error={!!errors.currentOrMostRecentEmployer}
                          errorText={errors.currentOrMostRecentEmployer?.message}
                        />
                      )}
                    />
                  </div>
                </div>
              )}

              <div className={isExecuSearch ? "grid grid-cols-[2fr_1fr] gap-2" : "grid grid-cols-1 gap-2"}>
                {isExecuSearch && (
                  <Controller
                    name="referralSource"
                    control={control}
                    render={({ field }) => (
                      <MultiSelect
                        label="How did you hear about this job?"
                        options={referralSourceOptions}
                        defaultLabel="Select referral source(s)"
                        selectedValues={field.value || []}
                        onSelect={(value, checked) => {
                          const current = field.value || [];
                          field.onChange(checked ? [...current, value] : current.filter((v: string) => v !== value));
                        }}
                        onClear={() => field.onChange([])}
                        skipShowAll
                        required
                        error={!!errors.referralSource}
                        errorText={errors.referralSource?.message}
                      />
                    )}
                  />
                )}
                <Controller
                  name="website"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Website (Optional)"
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors.website}
                      errorText={errors.website?.message}
                    />
                  )}
                />
              </div>

              <div>
                <div className="text-title-medium mb-2">Documents</div>
                {isExecuSearch ? (
                  <>
                    <div className="text-body-medium mb-2">
                      Please combine your cover letter, resume and other documents into one PDF file.
                    </div>
                    <FileUpload
                      label="Application Materials (PDF)*"
                      subtitle="Please upload a PDF file."
                      accept=".pdf"
                      maxSizeMB={5}
                      handleUpload={(file) => {
                        setValue("resume", file, { shouldValidate: true });
                        setResumeError(undefined);
                      }}
                      errorMsg={resumeError || errors.resume?.message}
                      onError={setResumeError}
                      onClear={() => {
                        resetField("resume");
                        setResumeError(undefined);
                      }}
                    />
                  </>
                ) : (
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <FileUpload
                        label="Resume*"
                        accept=".pdf"
                        maxSizeMB={5}
                        handleUpload={(file) => {
                          setValue("resume", file, { shouldValidate: true });
                          setResumeError(undefined);
                        }}
                        errorMsg={resumeError || errors.resume?.message}
                        onError={setResumeError}
                        onClear={() => {
                          resetField("resume");
                          setResumeError(undefined);
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <FileUpload
                        label="Cover Letter"
                        accept=".pdf"
                        maxSizeMB={5}
                        handleUpload={(file) => {
                          setValue("coverLetter", file, { shouldValidate: true });
                          setCoverLetterError(undefined);
                        }}
                        errorMsg={coverLetterError || errors.coverLetter?.message}
                        onError={setCoverLetterError}
                        onClear={() => {
                          resetField("coverLetter");
                          setCoverLetterError(undefined);
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {jobPost.applicationForm?.questions && jobPost.applicationForm.questions.length > 0 && (
                <div>
                  <DynamicQuestionRenderer questions={jobPost.applicationForm.questions} />
                </div>
              )}

              {errors.root && <p className="text-sys-brand-error text-body-medium">{errors.root.message}</p>}
            </form>
          </FormProvider>
        </div>
      </Dialog>
      <EmailPreviewDialog
        open={previewOpen}
        onClosed={() => setPreviewOpen(false)}
        subject={previewLoading ? undefined : preview?.subject}
        html={previewLoading ? undefined : preview?.html}
        loading={previewLoading}
        errorMessage={previewLoading ? null : (previewError?.message ?? null)}
      />
    </>
  );
}
